import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import {
  createBackdatedHoursRequest,
  getMyBackdatedHoursRequests,
  getPendingBackdatedHoursRequests,
  getApprovedBackdatedHoursRequests,
  approveBackdatedHoursRequest,
  correctBackdatedHoursEntry,
  declineBackdatedHoursRequest,
  deleteBackdatedHoursRequest,
  type BackdatedHoursEntryValuesInput,
  type CreateBackdatedHoursRequestInput,
  type DeclineBackdatedHoursRequestInput,
} from '../services/api/backdatedHoursRequests';
import { invalidateMany } from './queryUtils';

// Phase 113 (MKS-208) — mirrors `useJoinRequests.ts`.

/**
 * Caches every decision has to refresh: the requester's own list, the approval
 * queue, and the counts badge. Approval additionally writes an Entry, so the
 * time-entry caches go too — see `useApproveBackdatedHoursRequest`.
 */
function invalidateBackdatedRequestCaches(qc: QueryClient): void {
  invalidateMany(qc, [
    ['pendingBackdatedHoursRequests'],
    // MKS-266 — an approval moves a row OUT of the pending list and INTO the
    // correction list, so both have to be refetched.
    ['approvedBackdatedHoursRequests'],
    ['myBackdatedHoursRequests'],
    ['approvalCounts'],
  ]);
}

export function useMyBackdatedHoursRequests(state?: string) {
  return useQuery({
    queryKey: ['myBackdatedHoursRequests', state],
    queryFn: () => getMyBackdatedHoursRequests(state),
  });
}

export function usePendingBackdatedHoursRequests(scope?: 'all', enabled = true) {
  return useQuery({
    queryKey: ['pendingBackdatedHoursRequests', scope ?? 'managed'],
    queryFn: () => getPendingBackdatedHoursRequests(scope),
    enabled,
  });
}

export function useCreateBackdatedHoursRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateBackdatedHoursRequestInput) => createBackdatedHoursRequest(input),
    onSuccess: () => invalidateBackdatedRequestCaches(queryClient),
  });
}

/** MKS-266 — the correction surface's feed, one project at a time. */
export function useApprovedBackdatedHoursRequests(projectId: number, enabled = true) {
  return useQuery({
    queryKey: ['approvedBackdatedHoursRequests', projectId],
    queryFn: () => getApprovedBackdatedHoursRequests(projectId),
    enabled,
  });
}

export function useApproveBackdatedHoursRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    // MKS-266 — `input` carries the approver's adjustments; `{}` approves the
    // request as filed.
    mutationFn: ({ id, input }: { id: number; input?: BackdatedHoursEntryValuesInput }) =>
      approveBackdatedHoursRequest(id, input),
    onSuccess: () => {
      invalidateBackdatedRequestCaches(queryClient);
      // D-03 — approval WRITES the entry, so the hours the grid and the
      // timesheets show have just changed.
      invalidateMany(queryClient, [['timeEntries'], ['timesheets']]);
    },
  });
}

/** MKS-266 — correcting the entry an approval already created. */
export function useCorrectBackdatedHoursEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: BackdatedHoursEntryValuesInput }) =>
      correctBackdatedHoursEntry(id, input),
    onSuccess: () => {
      invalidateBackdatedRequestCaches(queryClient);
      invalidateMany(queryClient, [['timeEntries'], ['timesheets']]);
    },
  });
}

export function useDeclineBackdatedHoursRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: DeclineBackdatedHoursRequestInput }) =>
      declineBackdatedHoursRequest(id, input),
    onSuccess: () => invalidateBackdatedRequestCaches(queryClient),
  });
}

export function useDeleteBackdatedHoursRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteBackdatedHoursRequest(id),
    onSuccess: () => invalidateBackdatedRequestCaches(queryClient),
  });
}
